'use client';

import { Media } from '@/components/common/Media/Media';
import { type ProductSimple } from '@/domain/products/types';
import { useMediaQuery } from '@/hooks';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Drawer } from '@andritzot/metris-web-ui/navigation/drawer';
import ChevronLeftOutlined from '@mui/icons-material/ChevronLeftOutlined';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';

import { useHelpWizardStore } from '../../store/helpWizard';
import { useSolutionStore } from '../../store/solution';
import { type FilterList, type Filters } from '../../types';
import { HelpDialog } from './HelpWizard';
import ProductFilter from './ProductFilter';

interface SideFilterProps {
  filteredProducts: ProductSimple[];
  filtersValue: Filters[];
  filterList: FilterList;
  isInternal: boolean;
}

const SideFilter = ({ filteredProducts, filtersValue, filterList, isInternal }: SideFilterProps) => {
  const isSidebarOpen = useSolutionStore((s) => s.isSidebarOpen);
  const isDialogOpen = useHelpWizardStore((s) => s.isDialogOpen);
  const { isMobile } = useMediaQuery();

  const handleOpenHelpDialog = () => useHelpWizardStore.setState({ isDialogOpen: true });
  const handleCloseHelpDialog = () => useHelpWizardStore.setState({ isDialogOpen: false });
  const handleCloseSidebar = () => useSolutionStore.setState({ isSidebarOpen: false });

  return (
    <Box
      data-testid="side-filter"
      sx={{
        position: 'relative',
        minWidth: '20vw',
        marginLeft: '20px',
        boxShadow: '6px -4px 7px -8px #4f4f4f6e',
        borderRadius: '2px',
        display: isSidebarOpen ? undefined : 'none',
      }}
    >
      <Media greaterThanOrEqual="md">
        <Box
          sx={{
            position: 'fixed',
            width: '20vw',
            mt: 1,
            background: 'background.paper',
          }}
        >
          <Box
            sx={{
              width: '100%',
              pr: { md: '18px' },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', mb: '10px' }}>
              <Button
                variant="outlined"
                endIcon={<HelpOutlineOutlined />}
                onClick={handleOpenHelpDialog}
                sx={{ borderRadius: 0, width: '100%', justifyContent: 'left', mr: '0.5rem' }}
              >
                Need help?
              </Button>
              <IconButton variant="outlined" onClick={handleCloseSidebar} sx={{ color: 'text.primary' }}>
                <ChevronLeftOutlined />
              </IconButton>
            </Box>
          </Box>
          <ProductFilter
            filterList={filterList}
            filteredProducts={filteredProducts}
            filtersValue={filtersValue}
            isInternal={isInternal}
          />
        </Box>
      </Media>
      <Drawer anchor="left" open={isSidebarOpen && isMobile} onClose={handleCloseSidebar}>
        <ProductFilter
          filterList={filterList}
          filteredProducts={filteredProducts}
          filtersValue={filtersValue}
          isInternal={isInternal}
        />
      </Drawer>
      {isDialogOpen && <HelpDialog handleClose={handleCloseHelpDialog} />}
    </Box>
  );
};

export default SideFilter;
